import React, { useEffect, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import './quill-renderer.scss' // your custom SCSS
import { modules } from './quillModules' // import your config
import { Switch, Typography, Flex } from 'antd'

const { Text } = Typography;

// Check if HTML contains formatting (ignore basic p and br tags)
const hasFormatting = (html) => {
    if (!html) return false;
    
    // If there's no HTML content or it's just whitespace/empty, return false
    if (!html.trim()) return false;
    
    // Extract all HTML tags
    const tagRegex = /<\/?[^>]+>/g;
    const matches = html.match(tagRegex) || [];
    
    // If there are no tags, it's plain text
    if (matches.length === 0) return false;
    
    // Check each tag - if it's not a basic p or br tag, it's formatted
    for (const match of matches) {
        const lowerMatch = match.toLowerCase();
        
        // Skip checking the basic tags
        if (lowerMatch === '<p>' || 
            lowerMatch === '</p>' || 
            lowerMatch === '<br>' || 
            lowerMatch === '<br/>' || 
            lowerMatch === '</br>') {
            continue;
        }
        
        // If tag has attributes (like <p style="...">), it's formatted
        if (lowerMatch.startsWith('<p ')) {
            return true;
        }
        
        // Any other tag (not p or br) means it's formatted
        return true;
    }
    
    // If we only found basic p and br tags, it's not formatted
    return false;
};

// Helper function to clean Quill's empty state HTML
const cleanQuillHtml = (html) => {
    if (!html) {
        return '';
    }
    // Remove common empty tags and whitespace, check if result is empty
    const cleaned = html.replace(/<p><br><\/p>|<p><\/p>|<br\s*\/?>/gi, '').trim();
    return cleaned === '' ? '' : html;
};

const TextEditor = ({ value, onChange, readOnly, placeholder, label }) => {
    const ref = useRef(null)
    
    // Default to basic mode, but use advanced if content has formatting
    const [isAdvancedMode, setIsAdvancedMode] = useState(() => {
        return hasFormatting(value);
    });
    
    // Store HTML content
    const [htmlText, setHtmlText] = useState(value || '');

    // Update internal state when external value changes
    useEffect(() => {
        setHtmlText(value || '');
    }, [value]);

    // Handle toggle change
    const handleModeChange = (checked) => setIsAdvancedMode(checked);

    // Handle changes in editor
    const handleEditorChange = (content) => {
        setHtmlText(content);
        // Clean the content before passing it up
        const cleanedContent = cleanQuillHtml(content);
        onChange(cleanedContent);
    }

    useEffect(() => {
        const handleKeyDown = e => {
            // Example: Ctrl+M to blur
            if (e.ctrlKey && e.key === 'm') {
                ref.current?.blur()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Configure modules based on mode
    const basicModules = {
        toolbar: false, // No toolbar in basic mode
    };

    return (
        <div>
            <Flex justify="space-between" align="center" style={{ marginBottom: '8px' }}>
                {label && <Text>{label}</Text>}
                
                {!readOnly && (
                    <label>
                        <Switch 
                            checked={isAdvancedMode}
                            onChange={handleModeChange}
                            className='mr-4'
                            size='small'
                        />
                        <Text>Advanced</Text>
                    </label>
                )}
            </Flex>
            
            <ReactQuill
                ref={ref}
                theme='snow'
                value={htmlText}
                onChange={handleEditorChange}
                modules={isAdvancedMode ? modules : basicModules}
                placeholder={placeholder}
                readOnly={readOnly}
            />
        </div>
    )
}

export default TextEditor
